import { useEffect, useRef, useState } from "react";
import type { Attachment, AttachmentKindName } from "@dentacore/shared-types";
import { ATTACHMENT_KIND_LABELS, ATTACHMENT_KINDS, FULL_MOUTH } from "@dentacore/shared-types";

import { listAttachments, removeAttachment } from "../api/patients";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCan } from "../auth/use-can";
import { Modal } from "../ui/modal";
import styles from "./imaging-tab.module.css";
import { TEETH_LOWER, TEETH_UPPER } from "./tooth-geometry";
import { uploadAttachment, UploadRejected } from "./upload-file";
import { Pager, useFitPages } from "../ui/paging";
import { counted, DAY_GAP, DayCard, DayList, groupByDay } from "./by-day";
import { RequiredMark } from "../ui/required-mark";
import { useRingAsk } from "./ring-ask";
import type { RingAsk } from "./ring-ask";

/**
 * The Imaging tab — the mockup's *Imaging on file* panel.
 *
 * ── Two things the mockup shows that this does not ──────────────────────────
 *
 * **"Send to patient" is drawn, disabled, and says why** (P2 Q17): it sends by
 * WhatsApp, which is Phase 6. A disabled control that names what it is waiting
 * for promises nothing the product cannot yet do.
 *
 * **"Annotate" and "Compare" are not drawn at all**, and that is a deliberate
 * departure from the design spec. They differ from the button above in the way
 * that matters: no story anywhere in the plan builds them, so there is no
 * "yet" to point at. A disabled control for a feature nobody has scoped is a
 * promise rather than a placeholder.
 *
 * ── What multi-select is for ────────────────────────────────────────────────
 *
 * In the mockup it feeds *Send*. With Send disabled it would select nothing for
 * no purpose, so here it feeds **Remove selected** — the one bulk action this
 * screen genuinely has.
 *
 * **The table is the client's design and stays** (PF11) — one per day, inside
 * the day's container with the date on its left (PF9), in pages that fit the
 * window (PF4). A thumbnail grid was offered and declined.
 */
export function ImagingTab({
  patientId,
  openImageId,
  onOpened,
  onCount,
  ask,
  onAsked,
}: {
  patientId: string;
  /**
   * An image to open as soon as the list has loaded (client's manual QA, item
   * 29). *Recent visits* draws a thumbnail per image and had no click target at
   * all; rather than build a second viewer there, that row now brings you here
   * with the image already open — which is also where the download and delete
   * it might make you want to reach for live.
   */
  openImageId?: string | null;
  /** Cleared once honoured, so returning to this tab does not reopen it. */
  onOpened?: () => void;
  /** Told how many images the file has, so the tab can say (PF12). */
  onCount?: (count: number) => void;
  /** The quick ring's *Import images* (STORY-221): opens the importer, as the button does. */
  ask?: RingAsk<"images"> | null;
  /** Told once the ask has been honoured, so it is not honoured again. */
  onAsked?: () => void;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  // Importing and removing are their own permissions (QA third run).
  const can = useCan();
  const uploads = can("clinical.imaging.upload");
  const deletes = can("clinical.imaging.delete");
  const [images, setImages] = useState<Attachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [kind, setKind] = useState<AttachmentKindName>("PERIAPICAL");
  const [region, setRegion] = useState<string>(FULL_MOUTH);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  /** So the hand-off above fires once per request rather than on every load. */
  const honoured = useRef<string | null>(null);

  // The ring opens the importer; it never closes one already open, as the
  // button's second press would.
  useRingAsk(
    ask,
    images !== null,
    () => {
      if (uploads && busy === null) setImporting(true);
    },
    onAsked,
  );

  useEffect(() => {
    if (openImageId === undefined || openImageId === null) return;
    if (images === null || honoured.current === openImageId) return;

    const wanted = images.find((image) => image.id === openImageId);
    honoured.current = openImageId;
    if (wanted !== undefined) setViewing(wanted);
    onOpened?.();
  }, [openImageId, images, onOpened]);

  useEffect(() => {
    if (images !== null) onCount?.(images.length);
  }, [images, onCount]);

  const days =
    images === null ? [] : groupByDay(images, (image) => image.capturedAt ?? image.createdAt);
  // A day is its table's 31px header and a 45px row per image.
  const pages = useFitPages({
    keys: days.map((day) => day.key),
    estimate: (index) => Math.max(62, 33 + 45 * (days[index]?.rows.length ?? 0)),
    gap: DAY_GAP,
    resetOn: patientId,
  });
  const [start, end] = pages.range;
  const imagesBefore = (index: number): number =>
    days.slice(0, index).reduce((sum, day) => sum + day.rows.length, 0);

  const reload = (signal?: AbortSignal): Promise<void> =>
    listAttachments(patientId, signal).then((page) => {
      setImages(page.attachments);
      // A selection that outlived the rows it pointed at would let "Remove
      // selected" act on ids that no longer exist.
      setSelected((current) => {
        const live = new Set(page.attachments.map((image) => image.id));
        return new Set([...current].filter((id) => live.has(id)));
      });
    });

  useEffect(() => {
    const controller = new AbortController();

    setImages(null);
    setError(null);
    setSelected(new Set());
    setViewing(null);

    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(cause instanceof Error ? cause.message : "Could not read this file's imaging.");
    });

    return () => controller.abort();
  }, [patientId]);

  const apply = (label: string, work: () => Promise<unknown>): void => {
    setBusy(label);
    setError(null);

    work()
      .then(() => reload())
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That did not work.");
      })
      .finally(() => setBusy(null));
  };

  /**
   * Upload the chosen files one after another rather than all at once.
   *
   * Each one hashes its own bytes and draws its own thumbnail, both of which
   * are real work on the main thread. Firing a full-mouth series in parallel
   * makes the interface unresponsive for as long as it takes; in sequence the
   * screen stays usable and the total is much the same.
   */
  const onFiles = (files: FileList | null): void => {
    if (files === null || files.length === 0) return;
    const chosen = Array.from(files);

    apply("upload", async () => {
      for (const file of chosen) {
        try {
          await uploadAttachment(patientId, file, { kind, region });
        } catch (cause) {
          // One rejected file does not abandon the rest of the series — the
          // clinician gets the images that were fine and is told which was not.
          if (cause instanceof UploadRejected) setError(cause.message);
          else throw cause;
        }
      }
      setImporting(false);
    });
  };

  const toggle = (id: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeSelected = (): void => {
    const ids = [...selected];
    apply("remove", async () => {
      for (const id of ids) await removeAttachment(patientId, id);
      setSelected(new Set());
    });
  };

  return (
    <div
      className={styles.imaging}
      data-testid="imaging-tab"
      data-image-count={images === null ? undefined : images.length}
    >
      <div className={styles.head}>
        {uploads && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy !== null}
            onClick={() => setImporting((open) => !open)}
            data-testid="import-images"
          >
            Import images
          </button>
        )}
        {/*
          **A control appears when it can do something** (client, 2026-09-07:
          *"clear, remove selected, send to patients should not show unless an
          image is selected"*).

          The tab used to open with five buttons, four of them greyed — on a
          file with no images, which is every new patient. Four dead controls
          read as a broken screen rather than an empty one, and they crowded the
          one button that worked.

          Two tiers, because the conditions genuinely differ. **Select all**
          needs images to exist. **Clear, Remove selected and Send to patient**
          need a selection, and there is no selection until somebody makes one —
          so they are absent until then rather than drawn and dead.
        */}
        {images !== null && images.length > 0 && (
          <button
            type="button"
            className={styles.secondary}
            disabled={busy !== null}
            onClick={() => setSelected(new Set(images.map((image) => image.id)))}
            data-testid="select-all"
          >
            Select all
          </button>
        )}

        {selected.size > 0 && (
          <>
            <button
              type="button"
              className={styles.secondary}
              disabled={busy !== null}
              onClick={() => setSelected(new Set())}
              data-testid="clear-selection"
            >
              Clear
            </button>
            {deletes && (
              <button
                type="button"
                className={styles.secondary}
                disabled={busy !== null}
                onClick={removeSelected}
                data-testid="remove-selected"
              >
                Remove selected ({String(selected.size)})
              </button>
            )}
            {/*
              Still drawn disabled **with its reason in the title**, rather than
              hidden: this is a feature the mockup promises and Phase 6
              delivers, so it is a placeholder, not a dead control. It only
              reaches the screen once a selection exists, which is the only
              moment it would ever have applied.
            */}
            <button
              type="button"
              className={styles.secondary}
              disabled
              title="Sending images to a patient arrives with WhatsApp, in Phase 6."
              data-testid="send-to-patient"
            >
              Send to patient
            </button>
          </>
        )}
        {images !== null && images.length > 0 && (
          <span className={styles.count}>{counted(images.length, "image")}</span>
        )}
      </div>

      {uploads && importing && (
        <div className={styles.importer} data-testid="importer">
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Type
              <RequiredMark />
            </span>
            <select
              className={styles.select}
              value={kind}
              onChange={(event) => setKind(event.target.value as AttachmentKindName)}
              data-testid="import-kind"
            >
              {ATTACHMENT_KINDS.map((name) => (
                <option key={name} value={name}>
                  {ATTACHMENT_KIND_LABELS[name]}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Region</span>
            <select
              className={styles.select}
              value={region}
              onChange={(event) => setRegion(event.target.value)}
              data-testid="import-region"
            >
              <option value={FULL_MOUTH}>Full mouth</option>
              {[...TEETH_UPPER, ...TEETH_LOWER].map((fdi) => (
                <option key={fdi} value={String(fdi)}>
                  Tooth {fdi}
                </option>
              ))}
            </select>
          </label>

          <input
            type="file"
            className={styles.file}
            multiple
            accept="image/jpeg,image/png,application/pdf"
            aria-label="Choose images"
            disabled={busy !== null}
            onChange={(event) => onFiles(event.target.files)}
            data-testid="import-file"
          />
          <span className={styles.hint}>JPEG, PNG or PDF · up to 25 MB each</span>
        </div>
      )}

      {busy === "upload" && (
        <p className={styles.empty} data-testid="uploading">
          Uploading…
        </p>
      )}

      {error !== null && (
        <p className={styles.error} data-testid="imaging-error">
          {error}
        </p>
      )}

      {images === null && (
        <p className={styles.empty} data-testid="imaging-loading">
          Reading the file…
        </p>
      )}

      {images !== null && images.length === 0 && (
        <p className={styles.empty} data-testid="imaging-empty">
          No images on file yet — radiographs land here the moment they are captured.
        </p>
      )}

      {/*
        **Grouped by the day they were captured**, the same card History and
        Notes use (client, 2026-09-07). A set of radiographs is taken at one
        appointment — four bitewings in a sitting — and a flat table listed them
        as four unrelated rows with the same date repeated down a column.

        The Date column goes with the grouping: the header above the rows says
        it once. What is left in the table is what differs between images.
      */}
      {days.length > 0 && (
        <div className={styles.area} ref={pages.areaRef}>
          <DayList testId="image-days">
            {days.slice(start, end).map((day) => (
              <DayCard
                key={day.key}
                dayKey={day.key}
                label={day.label}
                count={counted(day.rows.length, "image")}
                measureRef={pages.measure(day.key)}
                flush
              >
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col" className={styles.tick}>
                          <span className={styles.srOnly}>Selected</span>
                        </th>
                        <th scope="col">Preview</th>
                        <th scope="col">Type</th>
                        <th scope="col">Region</th>
                        <th scope="col">Captured by</th>
                        <th scope="col">
                          <span className={styles.srOnly}>Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.rows.map((image) => (
                        <tr key={image.id} data-testid="image-row" data-image-id={image.id}>
                          <td className={styles.tick}>
                            <input
                              type="checkbox"
                              checked={selected.has(image.id)}
                              onChange={() => toggle(image.id)}
                              aria-label={`Select ${ATTACHMENT_KIND_LABELS[image.kind]}`}
                              data-testid="image-select"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className={styles.preview}
                              onClick={() => setViewing(image)}
                              aria-label={`Open ${ATTACHMENT_KIND_LABELS[image.kind]}`}
                              data-testid="image-open"
                            >
                              {image.thumbnailUrl === null ? (
                                // A PDF has no thumbnail — canvas cannot render one
                                // (P2 Q6) — so it takes a file mark rather than a
                                // broken image.
                                <span className={styles.fileMark} aria-hidden="true">
                                  PDF
                                </span>
                              ) : (
                                <img src={image.thumbnailUrl} alt="" className={styles.thumbnail} />
                              )}
                            </button>
                          </td>
                          <td data-testid="image-kind">{ATTACHMENT_KIND_LABELS[image.kind]}</td>
                          <td>{regionLabel(image.region)}</td>
                          <td>{image.capturedByName ?? "—"}</td>
                          <td>
                            {deletes && (
                              <button
                                type="button"
                                className={styles.secondary}
                                disabled={busy !== null}
                                onClick={() =>
                                  apply("remove", () => removeAttachment(patientId, image.id))
                                }
                                data-testid="image-delete"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DayCard>
            ))}
          </DayList>
        </div>
      )}

      <Pager
        page={pages.page}
        count={pages.pages.length}
        onPage={pages.setPage}
        from={imagesBefore(start) + 1}
        to={imagesBefore(end)}
        total={images?.length ?? 0}
        noun="images"
        testId="imaging-pager"
      />

      {viewing !== null && (
        <Modal
          label="Image viewer"
          testId="image-viewer"
          panelClassName={styles.viewer}
          onClose={() => {
            setViewing(null);
          }}
        >
          <div className={styles.viewerHead}>
            <div>
              <div className={styles.viewerRegion}>{regionLabel(viewing.region)}</div>
              <h3 className={styles.viewerTitle}>{ATTACHMENT_KIND_LABELS[viewing.kind]}</h3>
              <div className={styles.viewerMeta}>
                {formatDate(viewing.capturedAt ?? viewing.createdAt, timezone)} ·{" "}
                {viewing.capturedByName ?? "—"}
              </div>
            </div>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setViewing(null)}
              data-testid="viewer-close"
            >
              Close
            </button>
          </div>

          <div className={styles.stage}>
            {viewing.mimeType === "application/pdf" ? (
              <a
                className={styles.primary}
                href={viewing.downloadUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="viewer-download"
              >
                Open the document
              </a>
            ) : (
              <img
                src={viewing.downloadUrl}
                alt={`${ATTACHMENT_KIND_LABELS[viewing.kind]}, ${regionLabel(viewing.region)}`}
                className={styles.full}
                data-testid="viewer-image"
              />
            )}
          </div>

          <div className={styles.viewerActions}>
            {deletes && (
              <button
                type="button"
                className={styles.secondary}
                disabled={busy !== null}
                onClick={() => {
                  const id = viewing.id;
                  setViewing(null);
                  apply("remove", () => removeAttachment(patientId, id));
                }}
                data-testid="viewer-delete"
              >
                Remove image
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/** `FULL_MOUTH` reads as words; a tooth reads as a tooth. */
function regionLabel(region: string | null): string {
  if (region === null) return "—";
  return region === FULL_MOUTH ? "Full mouth" : `Tooth ${region}`;
}

function formatDate(iso: string, timeZone: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : // The location's day, not the browser's (P2 Q8).
      date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
      });
}
